import { Link } from 'react-router-dom'

const guide = {
  slug: 'is-screen-time-enough-to-block-porn',
  category: 'holds',
  kicker: 'Screen Time',
  title: 'Is Screen Time enough to block porn on iPhone? The five ways around it',
  seoTitle: 'Is Screen Time Enough to Block Porn on iPhone?',
  description: `Screen Time’s Limit Adult Websites is free and worth turning on. It is also undone by a passcode you set, or reset with your Apple ID. The five ways around it.`,
  published: '2026-08-25',
  modified: '2026-09-26',
  minutes: 6,
  intro: `Screen Time is the first thing anyone tries, and rightly: it's
          free, it's already on the phone, and it takes two minutes. The
          question isn't whether it blocks porn on a good day — it does.
          It's whether it holds on the night you'd undo it. Here are the
          five ways it gives, and what a lock has to add.`,
  next: ['how-to-block-porn-on-iphone', 'porn-blocker-you-cant-uninstall', 'does-incognito-bypass-porn-blockers'],
  body: (
    <>
      <h2>What Screen Time does well</h2>
      <p>
        Turn on <b>Settings → Screen Time → Content &amp; Privacy Restrictions
        → Content Restrictions → Web Content → Limit Adult Websites</b>, set
        a Screen Time passcode, and Safari refuses a large set of adult
        sites. Private Browsing disappears from Safari while the limit is
        on. You can add specific sites to "Never Allow". For free, on a
        Tuesday afternoon, that's a lot.
      </p>
      <p>Then the five ways it gives.</p>

      <h2>1. You know the passcode</h2>
      <p>
        The Screen Time passcode is four digits you chose. Turning the limit
        off is: Settings → Screen Time → passcode → toggle. Twenty seconds.
        The block holds precisely as long as you don't want to remove it —
        which is the one condition that doesn't matter.
      </p>

      <h2>2. Someone else knows it — and it's still resettable</h2>
      <p>
        The usual fix is to have a partner or friend set the code. Two
        problems. The code is now one text message away, on a night when
        you'll write that message. And Apple provides <b>Forgot Passcode?</b>,
        which resets the Screen Time passcode with the Apple ID signed in on
        the phone. That's your Apple ID. The lock has a spare key, and the
        spare key is in your pocket.
      </p>

      <h2>3. It's a website filter, not a device filter</h2>
      <p>
        "Limit Adult Websites" is applied to web content — Safari first, and
        apps that use the system web filter. It has nothing to say about
        what a social app, a messaging app or a forum app shows on its own
        screens. There is no shortage of doors that aren't a website.
      </p>

      <h2>4. It's a list, not a category</h2>
      <p>
        Apple's filter is good and it's maintained, but it identifies sites;
        it doesn't refuse a whole category at the network level. A
        DNS-level category filter answers every lookup for every app on the
        phone with "pornography, nudity, adult themes: refused" — no list
        to keep up with. See <Link to="/guides/does-incognito-bypass-porn-blockers">how
        DNS blocking differs from browser blocking</Link>.
      </p>

      <h2>5. It stops at the phone</h2>
      <p>
        Screen Time settings can sync across your Apple devices, but the
        Windows PC, the old laptop and the iPad you forgot about are as
        open as they were. A block with one door left open isn't a block.
      </p>

      <h2>What a lock has to add</h2>
      <p>
        None of this makes Screen Time useless — turn it on today. It makes
        it a <em>first layer</em>. What has to sit on top of it:
      </p>
      <ul>
        <li><b>A category filter at DNS level</b>, so every app, every browser and every private window gets the same answer.</li>
        <li><b>No key in your pocket.</b> On iPhone and iPad that means a supervised profile: no Remove Profile button, no passcode to guess, and adding a workaround VPN is forbidden by the profile itself. Coming off is a deliberate request from your account with a cooling-off delay — not a toggle at 2am.</li>
        <li><b>A lock that only tightens.</b> Adding a locked day or a curfew applies at once; loosening waits twenty-four hours; the floor never drops.</li>
        <li><b>Every device</b>, one account: iPhone, iPad, Mac, PC.</li>
      </ul>
      <p>
        That is what SELFCNTRL is: the DNS category filter, on a supervised
        iPhone, with a night curfew and a pace you pick once. Ten minutes to
        set up, once. Full steps in <Link to="/guides/how-to-block-porn-on-iphone">the
        iPhone guide</Link>. And if you manage to strip it off a device we
        locked, we pay you $100 — see the <Link to="/remboursement">refund policy</Link>.
      </p>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>Screen Time</th><th>SELFCNTRL</th></tr>
        </thead>
        <tbody>
          <tr><td>Every app, not just Safari</td><td>No</td><td>Yes</td></tr>
          <tr><td>Private browsing</td><td>Disabled</td><td>Irrelevant (DNS level)</td></tr>
          <tr><td>Can be turned off instantly</td><td>Yes (passcode / Apple ID reset)</td><td>No (supervised profile + delay)</td></tr>
          <tr><td>Blocks adding a VPN</td><td>No</td><td>Yes, on iPhone / iPad</td></tr>
          <tr><td>Night curfew</td><td>Downtime (same passcode)</td><td>Yes, internet cut on schedule</td></tr>
          <tr><td>Mac and PC too</td><td>Apple devices only</td><td>Yes</td></tr>
          <tr><td>Price</td><td>Free</td><td>$9.99/mo or $49.99/year</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'Can Screen Time block porn completely?',
      a: `It blocks a large set of adult sites in Safari and turns off
          Private Browsing, which is worth having. It doesn't cover what apps
          display, and it's undone by a passcode you set or can reset with
          your Apple ID — so it holds until the night you'd remove it.`
    },
    {
      q: 'Should I turn Screen Time on as well as SELFCNTRL?',
      a: `You can; there's no conflict. SELFCNTRL works at DNS level and
          through a supervised profile, Screen Time at the browser level with
          a passcode. The first does the holding; the second is a free extra
          layer.`
    },
    {
      q: 'Is Downtime the same as a curfew?',
      a: `Downtime limits apps during hours you set, behind the same Screen
          Time passcode. A SELFCNTRL curfew cuts the internet on the device
          for those hours, and can't be lifted with a code.`
    }
  ]
}

export default guide
